import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useCallback, useState } from "react";

import { useCoachViewAccount } from "~/components/nova/use-coach-view-account";
import {
  NOVA_SUBSCRIPTION_QUERY_KEY,
  useNovaAccess,
} from "~/components/nova/use-nova-access";
import {
  READINESS_COMPONENTS_INTRO,
  READINESS_HERO,
  READINESS_STEPS_TITLE,
  ReadinessComponentTiles,
  ReadinessPreviewCard,
  ReadinessSteps,
} from "~/components/readiness/readiness-landing-sections";
import {
  READINESS_RESULTS_VIDEO_QUERY,
  ReadinessNovaOffer,
  ReadinessResultsVideo,
  ReadinessStrategyCall,
  useNovaTrialFinePrint,
} from "~/components/readiness/readiness-offer";
import {
  accountReadinessDraftKey,
  ReadinessQuestionnaire,
} from "~/components/readiness/readiness-questionnaire";
import {
  ReadinessComponentList,
  ReadinessSummary,
  useReadinessView,
} from "~/components/readiness/readiness-score";
import { useReadinessQuestionEvents } from "~/components/readiness/use-readiness-question-events";
import { Button, ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { PageSection } from "~/components/ui/page-section";
import { PageHeader } from "~/components/ui/page-title";
import { createNovaCheckoutSession } from "~/lib/functions/nova-billing";
import {
  completeMyReadinessFn,
  startMyReadinessFn,
} from "~/lib/functions/readiness";
import { useReturnPath } from "~/lib/hooks/use-return-path";
import {
  trackReadinessResultRevealed,
  trackReadinessStarted,
} from "~/lib/monitoring/track-event";
import { callsAndLinksQueryOptions } from "~/lib/queries/callsAndLinksQuery";
import { myReadinessQueryOptions } from "~/lib/queries/readinessQuery";
import type { CoachAccount } from "~/lib/shared/coach-account";
import { formatNovaPrice } from "~/lib/shared/nova/format-price";
import { READINESS_SCORE_NAME } from "~/lib/shared/readiness/config";
import type { ReadinessView } from "~/lib/shared/readiness/describe";
import type { ReadinessResult } from "~/lib/shared/readiness/engine";
import {
  offersReadinessResultsVideo,
  readinessNextStep,
  type ReadinessNextStep,
} from "~/lib/shared/readiness/next-step";
import {
  isReadinessRerunRefusedSearch,
  READINESS_RERUN_REFUSED_NOTICE,
  readinessRerun,
  type ReadinessRerun,
} from "~/lib/shared/readiness/rerun";
import { UserFacingError } from "~/lib/shared/user-facing-error";

export const Route = createFileRoute(
  "/_user/my-coaching/business/readiness-score",
)({
  staticData: { title: READINESS_SCORE_NAME },
  loader: async ({ context }) => {
    const { queryClient } = context;
    // The strategy call reads the Book-a-call link. Without it the call stays
    // the email, so a failed read is no error page. A pre-trial coach's result can show the results video, so
    // the page opens knowing whether one is set; a failed read is a result
    // without it. The section still waits for her NOVA access read
    // (`useNovaAccess`), which the browser makes after hydration: for a coach
    // without NOVA it lists the price from Stripe, so it stays off the
    // server render, and the section appears above her progression when it
    // answers.
    const [readiness] = await Promise.all([
      queryClient.ensureQueryData(myReadinessQueryOptions()),
      queryClient
        .ensureQueryData(callsAndLinksQueryOptions())
        .catch(() => undefined),
      context.account?.lifecycle === "pre_trial"
        ? queryClient
            .ensureQueryData(READINESS_RESULTS_VIDEO_QUERY)
            .catch(() => undefined)
        : undefined,
    ]);
    return readiness;
  },
  component: ReadinessScorePage,
});

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function ReadinessScorePage(): React.JSX.Element {
  const { data } = useSuspenseQuery(myReadinessQueryOptions());
  const queryClient = useQueryClient();
  const [taking, setTaking] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Kept apart from `notice`: a failure has to read as one and appear beside the
  // button that failed. While both shared a quiet grey line at the top of the
  // page, a coach whose start errored 16 times running reported it as "I click
  // on Start free assessment and nothing happens" (Sentry DCM-V4).
  const [startError, setStartError] = useState<string | null>(null);
  const latest = data.runs[0];
  const account = useCoachViewAccount();
  const lifecycle = account?.lifecycle ?? null;
  // She arrived from a public result the rerun rule kept out of her account.
  const rerunRefused = useLocation({
    select: (location) => isReadinessRerunRefusedSearch(location.searchStr),
  });
  const shownNotice =
    notice ?? (rerunRefused ? READINESS_RERUN_REFUSED_NOTICE : null);

  const start = useMutation({
    mutationFn: () => startMyReadinessFn(),
    onSuccess: (outcome) => {
      setStartError(null);
      if (outcome.kind === "not_due") {
        setNotice(
          `You can rerun your score from ${DATE_FORMAT.format(new Date(outcome.dueAt))}.`,
        );
        return;
      }
      trackReadinessStarted("signed_in");
      setNotice(null);
      setTaking(true);
    },
    onError: (error) => {
      setNotice(null);
      // The server writes the messages that name a cause a coach can act on —
      // an account already linked elsewhere, a rate limit. Anything else is
      // unexpected and gets copy that does not pretend to explain it.
      setStartError(
        error instanceof UserFacingError
          ? error.message
          : "We couldn't start the assessment. Please try again.",
      );
    },
  });

  const finish = useCallback(
    async (result: ReadinessResult) => {
      trackReadinessResultRevealed({
        surface: "signed_in",
        band: result.band,
        holding_back: result.holdingBack,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: myReadinessQueryOptions().queryKey,
        }),
        // `hasScore` in the NOVA payload drives the dashboard and studio.
        queryClient.invalidateQueries({
          queryKey: NOVA_SUBSCRIPTION_QUERY_KEY,
        }),
      ]);
      setTaking(false);
      window.scrollTo({ top: 0 });
    },
    [queryClient],
  );

  if (taking) {
    return (
      <InAppQuestions onResult={finish} onCancel={() => setTaking(false)} />
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        actions={
          latest ? (
            <Rerun
              rerun={readinessRerun({
                nextRerunAt: data.nextRerunAt,
                lifecycle,
              })}
              lastTakenAt={latest.completedAt}
              canStartTrial={lifecycle === "pre_trial"}
              starting={start.isPending}
              startError={startError}
              onStart={() => start.mutate()}
            />
          ) : null
        }
        title={READINESS_SCORE_NAME}
      />

      {shownNotice ? (
        <p role="status" className="text-text-secondary text-sm">
          {shownNotice}
        </p>
      ) : null}

      {latest ? (
        <ScoredView result={latest.result} runs={data.runs} account={account} />
      ) : (
        <NoScore
          hadRetiredScore={data.hadRetiredScore}
          starting={start.isPending}
          startError={startError}
          onStart={() => start.mutate()}
        />
      )}
    </div>
  );
}

/**
 * The rerun button, or, when her plan cannot rerun, the date a paid plan
 * would let her. That line is not a button: the offer below the score is the
 * page's one call to action.
 */
function Rerun({
  rerun,
  lastTakenAt,
  canStartTrial,
  starting,
  startError,
  onStart,
}: {
  rerun: ReadinessRerun;
  lastTakenAt: Date;
  /** A coach who has not started her trial can open the rerun with it. */
  canStartTrial: boolean;
  starting: boolean;
  startError: string | null;
  onStart: () => void;
}): React.JSX.Element {
  const lastTaken = `Last taken ${DATE_FORMAT.format(new Date(lastTakenAt))}`;
  if (rerun.kind === "needs_paid_plan") {
    const plan = canStartTrial
      ? "your trial or a paid NOVA plan"
      : "a paid NOVA plan";
    return (
      <div className="flex max-w-xs flex-col items-end gap-1 text-right">
        <p className="text-text-secondary text-sm text-pretty">
          {rerun.opensAt
            ? `With ${plan}, you can rerun your score from ${DATE_FORMAT.format(new Date(rerun.opensAt))}.`
            : `With ${plan}, you can rerun your score now.`}
        </p>
        <p className="text-text-tertiary text-xs">{lastTaken}</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        onClick={onStart}
        loading={starting}
        disabled={rerun.kind === "not_due"}
      >
        Rerun my score
      </Button>
      <p className="text-text-tertiary text-xs">
        {rerun.kind === "not_due"
          ? `Available from ${DATE_FORMAT.format(new Date(rerun.dueAt))}`
          : lastTaken}
      </p>
      {startError ? (
        <p role="alert" className="max-w-xs text-right text-sm text-red-700">
          {startError}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The pitch for a coach without a score: the public landing's hero, preview
 * card, six tiles and three steps, without the proof that persuades visitors
 * who do not know us yet. One start button, so a failed start shows its error
 * beside the button that failed.
 */
function NoScore({
  hadRetiredScore,
  starting,
  startError,
  onStart,
}: {
  hadRetiredScore: boolean;
  starting: boolean;
  startError: string | null;
  onStart: () => void;
}): React.JSX.Element {
  return (
    <>
      <section className="grid items-center gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
        <div className="space-y-6">
          <div className="space-y-3">
            <p className="eyebrow text-purple-700">{READINESS_HERO.eyebrow}</p>
            <h2 className="font-heading text-3xl leading-tight font-medium tracking-tight text-balance text-gray-900 sm:text-4xl">
              {READINESS_HERO.title}
              <span className="mt-2 block text-[0.6em] leading-tight tracking-normal text-purple-700">
                {READINESS_HERO.subtitle}
              </span>
            </h2>
          </div>
          <p className="text-text-secondary max-w-[52ch] text-lg text-pretty">
            {READINESS_HERO.intro}
          </p>
          {hadRetiredScore ? (
            <p className="text-text-secondary text-sm text-pretty">
              Your earlier Business Health Score used different questions, so it
              can't carry over. This takes about two minutes.
            </p>
          ) : null}
          <div className="space-y-2">
            <Button
              variant="primary"
              size="lg"
              onClick={onStart}
              loading={starting}
              className="max-sm:w-full"
            >
              Start free assessment
              <ArrowRight aria-hidden="true" />
            </Button>
            {startError ? (
              <p role="alert" className="max-w-md text-sm text-red-700">
                {startError}
              </p>
            ) : (
              <p className="text-text-tertiary text-sm">
                Takes two minutes, no typing. Free. Only you and the Dream Coach
                Match team can see your answers.
              </p>
            )}
          </div>
        </div>
        <ReadinessPreviewCard />
      </section>

      <PageSection
        title={READINESS_COMPONENTS_INTRO.title}
        description={READINESS_COMPONENTS_INTRO.body}
      >
        <ReadinessComponentTiles />
      </PageSection>

      <PageSection
        title={READINESS_STEPS_TITLE}
        className="rounded-2xl bg-purple-50 p-6 sm:p-8"
      >
        <ReadinessSteps />
      </PageSection>
    </>
  );
}

function InAppQuestions({
  onResult,
  onCancel,
}: {
  onResult: (result: ReadinessResult) => Promise<void>;
  onCancel: () => void;
}): React.JSX.Element {
  const clerkUserId = Route.useRouteContext({
    select: (context) => context.user.clerkUserId,
  });
  const { onViewed, onAnswered } = useReadinessQuestionEvents("signed_in");

  return (
    <div className="flex flex-col gap-4">
      <ReadinessQuestionnaire
        draftKey={accountReadinessDraftKey(clerkUserId)}
        onQuestionViewed={onViewed}
        onQuestionAnswered={onAnswered}
        onSubmit={async (answers) => {
          const outcome = await completeMyReadinessFn({ data: { answers } });
          if (outcome.kind === "not_due") {
            throw new UserFacingError(
              `You can rerun your score from ${DATE_FORMAT.format(new Date(outcome.dueAt))}.`,
            );
          }
          if (outcome.kind === "not_started") {
            throw new UserFacingError(
              "This assessment timed out. Start it again from your score page.",
            );
          }
          await onResult(outcome.result);
        }}
      />
      <div className="mx-auto w-full max-w-2xl">
        <Button variant="link" className="h-auto p-0" onClick={onCancel}>
          Back to my score page
        </Button>
      </div>
    </div>
  );
}

/** Her account, or null until it is read. */
type ViewAccount = Pick<CoachAccount, "lifecycle" | "suspended"> | null;

function ScoredView({
  result,
  runs,
  account,
}: {
  result: ReadinessResult;
  runs: readonly { completedAt: Date; result: ReadinessResult }[];
  account: ViewAccount;
}): React.JSX.Element {
  const view = useReadinessView(result);
  const next = useNextStep(view, account);
  return (
    <>
      <Card>
        <ReadinessSummary result={result} headingLevel="h2" />
      </Card>
      {next.withResultsVideo ? (
        <ReadinessResultsVideo
          action={next.action}
          fine={next.fine}
          variant="app"
        />
      ) : null}
      <PageSection description={view.summary} title={view.progressionTitle}>
        <ReadinessComponentList result={result} />
      </PageSection>
      {view.isPastSixFigures ? (
        <ReadinessStrategyCall
          bookACallUrl={next.bookACallUrl}
          trialAction={next.action}
          variant="app"
        />
      ) : (
        <ReadinessNovaOffer
          startWith={view.startWith}
          action={next.action}
          fine={next.fine}
          offersStrategyCall={view.offersStrategyCall}
          bookACallUrl={next.bookACallUrl}
          variant="app"
        />
      )}
      {runs.length > 1 ? <History runs={runs} /> : null}
    </>
  );
}

/**
 * The way forward from her score. `readinessNextStep` picks it by her account
 * state, and there is none until her account is read. When that step starts
 * her free trial, the results video leads with the same button under it, as on
 * the public results page.
 */
function useNextStep(
  view: ReadinessView,
  account: ViewAccount,
): {
  action: React.ReactNode;
  fine: string | null;
  /** The admin's Book-a-call link; null or undefined when none is set or read. */
  bookACallUrl: string | null | undefined;
  withResultsVideo: boolean;
} {
  const { data: nova } = useNovaAccess();
  const trialFine = useNovaTrialFinePrint();
  const returnTo = useReturnPath();
  const checkout = useMutation({
    mutationFn: () => createNovaCheckoutSession({ data: { returnTo } }),
    onSuccess: ({ url }) => window.location.assign(url),
  });

  // The strategy call opens the admin's Book-a-call link once one is set.
  // Until then it stays the email to the team.
  const { data: links } = useQuery(callsAndLinksQueryOptions());
  const hasNova = nova !== undefined && nova.access !== "none";
  const step = readinessNextStep({
    lifecycle: account?.lifecycle ?? null,
    trialEligible: nova?.trialEligible,
    suspended: account?.suspended ?? false,
  });
  // Past $100K the strategy call is the main button and her next step is a
  // link in a sentence below it, so the page keeps one main button.
  const inline = view.isPastSixFigures;

  const fine = checkout.isError
    ? "We couldn't open checkout. Try again."
    : step === null
      ? null
      : step.kind === "plans_and_pricing"
        ? step.fine
        : nova === undefined
          ? null
          : nova.access === "trialing"
            ? "Your trial is running. Nothing to pay until it ends."
            : hasNova
              ? "Included in your plan."
              : nova.trialEligible
                ? trialFine
                : nova.price
                  ? `${formatNovaPrice(nova.price)}. Cancel any time.`
                  : "Cancel any time.";

  return {
    action: step ? (
      <NextStepAction
        step={step}
        inline={inline}
        returnTo={returnTo}
        checkingOut={checkout.isPending}
        canCheckOut={nova !== undefined}
        onCheckout={() => checkout.mutate()}
      />
    ) : null,
    fine,
    bookACallUrl: links?.bookACallUrl,
    withResultsVideo:
      step !== null &&
      offersReadinessResultsVideo({
        step,
        trialEligible: nova?.trialEligible,
        pastSixFigures: view.isPastSixFigures,
      }),
  };
}

/** Her next step as the page's main button, or as a link inside a sentence. */
function NextStepAction({
  step,
  inline,
  returnTo,
  checkingOut,
  canCheckOut,
  onCheckout,
}: {
  step: ReadinessNextStep;
  inline: boolean;
  /** The screen checkout and billing come back to. */
  returnTo: string | undefined;
  checkingOut: boolean;
  /** False until the NOVA access read says whether checkout is a trial. */
  canCheckOut: boolean;
  onCheckout: () => void;
}): React.JSX.Element {
  const look = inline
    ? ({
        variant: "link",
        className: "h-auto p-0 align-baseline text-base text-purple-700",
      } as const)
    : ({ variant: "primary", size: "lg" } as const);
  switch (step.kind) {
    case "checkout":
      return (
        <Button
          {...look}
          loading={checkingOut}
          disabled={!canCheckOut}
          onClick={onCheckout}
        >
          {step.label}
        </Button>
      );
    case "asset":
      return (
        <ButtonRouter
          {...look}
          to="/my-coaching/business/nova/assets/$assetKey"
          params={{ assetKey: step.asset }}
        >
          {step.label}
        </ButtonRouter>
      );
    case "plans_and_pricing":
      return (
        <ButtonRouter
          {...look}
          to="/settings/profile/billing"
          search={{ returnTo }}
        >
          {step.label}
        </ButtonRouter>
      );
    default: {
      const exhaustive: never = step;
      throw new Error(`Unknown next step: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** Every rerun is kept, so she can see what moved. */
function History({
  runs,
}: {
  runs: readonly { completedAt: Date; result: ReadinessResult }[];
}): React.JSX.Element {
  return (
    <PageSection
      description="Every rerun is kept, so you can see what moved."
      title="History"
    >
      <Card className="gap-0 p-0">
        <ol className="divide-y divide-gray-100">
          {runs.map((run, index) => (
            <HistoryRow
              key={new Date(run.completedAt).toISOString()}
              run={run}
              previous={runs[index + 1]?.result ?? null}
            />
          ))}
        </ol>
      </Card>
    </PageSection>
  );
}

function HistoryRow({
  run,
  previous,
}: {
  run: { completedAt: Date; result: ReadinessResult };
  previous: ReadinessResult | null;
}): React.JSX.Element {
  const view = useReadinessView(run.result);
  const delta =
    previous?.score != null && run.result.score !== null
      ? run.result.score - previous.score
      : null;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
      <span className="text-text-primary w-10 text-xl font-medium tabular-nums">
        {view.score ?? "—"}
      </span>
      <span
        className={
          delta === null || delta === 0
            ? "text-text-tertiary w-12 text-sm tabular-nums"
            : delta > 0
              ? "w-12 text-sm text-green-700 tabular-nums"
              : "w-12 text-sm text-amber-700 tabular-nums"
        }
      >
        {delta === null ? "" : delta > 0 ? `+${delta}` : String(delta)}
      </span>
      <span className="text-text-secondary min-w-0 flex-1 text-sm">
        {view.bandLabel} · {view.countLabel}
      </span>
      <span className="text-text-tertiary text-sm">
        {DATE_FORMAT.format(new Date(run.completedAt))}
      </span>
    </li>
  );
}
